import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  faAddressCard,
  faCircleQuarterStroke,
  faBookOpenCover as falBookOpenCover,
  faCloudUpload,
  faCheck,
} from '@fortawesome/pro-light-svg-icons'
import {
  faBookHeart,
  faBookOpenCover as farBookOpenCover,
} from '@fortawesome/pro-regular-svg-icons'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { useAnalyticsTrack } from '@/features/amplitude'

import { DATE_FORMATS } from '@/utils/dateHelpers'
import { getFinancialProfile } from '@/selectors/user.selectors'
import {
  getEstimatesByYear,
  getUnpaidTaxEstimatesByYear,
} from '@/features/taxes/quarterly/userTaxEstimates.selector'
import {
  ENSURE_USER_TAX_ESTIMATES_KEY,
  ensureUserTaxEstimates,
} from '@/features/taxes/quarterly/userTaxEstimates.slice'
import {
  findEstimateCoverage,
  hasGenerableGap,
} from '@/features/taxes/quarterly/estimateCoverage'
import { PreviousTaxPayments } from '@/features/taxes/taxes-profile/form-flow/PreviousTaxPayments'
import { splitTaxYearQuarter } from '@/features/taxes/quarterly/helpers'
import { updateFinancialProfile } from '@/actions/financialProfileActions'
import { dateWithoutTimestamp } from '@/features/taxes/quarterly/service'

import { selectIsFetchingForKeys } from '@/reducers/fetch'
import { ViewMissingStatementsAccordion } from '@/components/ViewMissingStatementsAccordion'
import {
  FETCH_NEEDED_BANK_STATEMENTS_FOR_CURRENT_TAX_QUARTER,
  getNeededBankStatementsForCurrentTaxQuarter,
} from '@/features/user/your-practice/documents/lib/userDocuments.slice'

import { TextColors, TypographyStyles } from '@/styles'
import {
  Button,
  Card,
  Icon,
  Link,
  Text,
  Dialog,
  List,
  CardColors,
  Loader,
} from '@/ui'

import {
  selectActiveQuarterlyTaxEstimateDetails,
  selectHasUserStartedQteWizardThisQuarter,
  selectIsPastTaxEstimateCalculationDate,
  selectQuarterlyTaxEstimateDetailsByYear,
} from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'

const checklistItemIconClasses = (pastDue: boolean) => {
  return `${pastDue ? TextColors.DANGER : TextColors.MOSS} min-h-[50px] min-w-[50px] my-2`
}

const addressCardIcon = (pastDue: boolean) => (
  <Icon className={checklistItemIconClasses(pastDue)} icon={faAddressCard} />
)

const quarterIcon = (pastDue: boolean) => (
  <Icon
    className={checklistItemIconClasses(pastDue)}
    icon={faCircleQuarterStroke}
    size="1x"
  />
)

const bookIcon = (pastDue: boolean) => (
  <Icon
    className={checklistItemIconClasses(pastDue)}
    icon={falBookOpenCover}
    size="1x"
  />
)

const uploadIcon = (pastDue: boolean) => (
  <Icon
    className={checklistItemIconClasses(pastDue)}
    icon={faCloudUpload}
    size="1x"
  />
)

export const ChecklistItemCard = ({
  isDone,
  icon,
  header,
  isStarted,
  isLoading,
  children,
  onClick,
}: {
  isDone: boolean
  icon: React.ReactNode
  header: React.ReactNode
  isStarted?: boolean
  isLoading?: boolean
  children?: React.ReactNode
  onClick?: () => void
}) => {
  return (
    <Card color={isDone ? CardColors.LEVEL_0 : CardColors.LEVEL_1}>
      <div className="flex gap-4 items-center">
        <div className="flex-shrink-0">{icon}</div>
        <div className="flex-1">
          <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-between">
            <div className="flex flex-col">
              <Text variant={TypographyStyles.H3} className="mb-2">
                {header}
              </Text>
              {children}
            </div>
            <div className="w-full sm:w-40">
              <Button
                variant={isDone ? 'secondary' : 'primary'}
                disabled={isDone || isLoading}
                onClick={onClick}
                className="w-full"
              >
                {isDone ? (
                  <>
                    <Icon icon={faCheck} className="mr-2" /> Done
                  </>
                ) : isStarted ? (
                  'Continue'
                ) : (
                  'Start'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}

export const DeadlineText = ({
  isDone,
  onEditClick,
  editLabel,
  pendingText,
}: {
  isDone: boolean
  onEditClick: () => void
  editLabel: string
  pendingText?: string
}) => {
  const isPastCalculationDate = useReselector(
    selectIsPastTaxEstimateCalculationDate
  )
  if (isPastCalculationDate) return null
  if (isDone) {
    return (
      <Text color={TextColors.MOSS}>
        <Button onClick={onEditClick} variant="link">
          Edit
        </Button>{' '}
        {editLabel}
      </Text>
    )
  }
  return pendingText ? <Text color={TextColors.MOSS}>{pendingText}</Text> : null
}

export const UpdateTaxInfoCTA = ({ isDone }: { isDone: boolean }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const fp = useReselector(getFinancialProfile)
  const started = useReselector(selectHasUserStartedQteWizardThisQuarter)
  const isPastCalc = useReselector(selectIsPastTaxEstimateCalculationDate)

  const onEditClick = () => {
    dispatch(
      updateFinancialProfile(fp?.id, {
        qteWizardLastStartedAt: dateWithoutTimestamp().format(
          DATE_FORMATS.TIMESTAMP
        ),
      })
    )
    navigate('/taxes/profile')
  }

  return (
    <ChecklistItemCard
      isDone={isDone}
      isStarted={started}
      icon={addressCardIcon(isPastCalc)}
      header="Update your income and withholdings"
      onClick={onEditClick}
    >
      <DeadlineText
        onEditClick={onEditClick}
        isDone={isDone}
        editLabel="income and withholdings information"
        pendingText="You’ll need your most recent paystub."
      />
    </ChecklistItemCard>
  )
}

export const UpdateQTEPaymentsCTA = ({ isDone }: { isDone: boolean }) => {
  const [open, setOpen] = useState(false)
  const dispatch = useAppDispatch()
  const ensuring = useReselector(selectIsFetchingForKeys, [
    ENSURE_USER_TAX_ESTIMATES_KEY,
  ])
  const activeQuarter = useReselector(selectActiveQuarterlyTaxEstimateDetails)
  const isPastCalc = useReselector(selectIsPastTaxEstimateCalculationDate)
  const financialProfile = useReselector(getFinancialProfile)
  const estimatesForYear = useReselector(
    getEstimatesByYear,
    activeQuarter?.taxYear
  )
  const detailsForYear = useReselector(
    selectQuarterlyTaxEstimateDetailsByYear,
    activeQuarter?.taxYear ?? ''
  )
  const coverage = findEstimateCoverage({
    estimates: estimatesForYear,
    taxYear: activeQuarter?.taxYear ?? '',
    expectedPerType: Object.keys(detailsForYear).length,
    homeState: financialProfile?.homeState,
  })
  const unpaidQuarters = useReselector(
    getUnpaidTaxEstimatesByYear,
    activeQuarter?.taxYear
  ).reduce((s, e) => s.add(e.taxQuarter), new Set<string>())
  const formattedEstimates = useMemo(() => {
    if (!activeQuarter) return null
    return (
      <List bulleted>
        {[...unpaidQuarters].map((quarterKey) => {
          const { quarter, year } = splitTaxYearQuarter(quarterKey)
          return (
            <List.Item key={quarterKey}>
              <Text color={TextColors.MOSS}>
                <b>
                  Q{quarter} {year}
                </b>
              </Text>
            </List.Item>
          )
        })}
      </List>
    )
  }, [activeQuarter, unpaidQuarters])

  // Opening with no estimates shows a dialog with nothing to enter, so create
  // them first. Gated on the same coverage rule the API applies.
  const openPayments = async () => {
    if (hasGenerableGap(coverage)) {
      await dispatch(ensureUserTaxEstimates())
    }
    setOpen(true)
  }

  return (
    <>
      <ChecklistItemCard
        isDone={isDone}
        isLoading={ensuring}
        icon={quarterIcon(isPastCalc)}
        header="Enter your quarterly tax payments"
        onClick={openPayments}
      >
        {!isDone && (
          <>
            <Text color={TextColors.MOSS}>
              Enter payments for the following quarters:
            </Text>
            <div className="ml-2 mt-2">{formattedEstimates}</div>
          </>
        )}
        <DeadlineText
          onEditClick={openPayments}
          isDone={isDone}
          editLabel="quarterly tax payments"
        />
      </ChecklistItemCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <Dialog.Content className="pt-5 max-w-2xl">
          <PreviousTaxPayments
            taxYear={Number(activeQuarter?.taxYear ?? new Date().getFullYear())}
            taxQuarter={Number(activeQuarter?.taxQuarter ?? 1)}
            heading="Update your quarterly tax payments to receive your estimated tax payment"
            explainer="Updating your quarterly tax payments helps Heard calculate a more accurate estimate for you: we predict how much you'll owe in taxes for this year, and then subtract actual payments already made."
            completesChecklistItem
          />
          <Dialog.Footer>
            <Dialog.Close asChild>
              <Button>Close</Button>
            </Dialog.Close>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </>
  )
}

export const UploadNeededBankStatementsCTA = () => {
  const track = useAnalyticsTrack()
  const needed = useFetchResponse(getNeededBankStatementsForCurrentTaxQuarter)
  const isPastCalc = useReselector(selectIsPastTaxEstimateCalculationDate)
  const total = needed?.reduce((acc, s) => acc + s.statements.length, 0) ?? 0
  const isDone = Boolean(needed && total === 0)
  const navigate = useNavigate()
  const fetching = useReselector(selectIsFetchingForKeys, [
    FETCH_NEEDED_BANK_STATEMENTS_FOR_CURRENT_TAX_QUARTER,
  ])

  const content = useMemo(() => {
    if (fetching) return <Loader />
    if (isDone) {
      return (
        <Text>
          Great job! You are all caught up.{' '}
          <Link href="/practice/documents?tab=bank" variant="secondary">
            View Your Documents
          </Link>
        </Text>
      )
    }
    return (
      <>
        <Text>Upload missing statements</Text>
        <Text>
          Help us complete your bookkeeping by uploading missing <b>business</b>{' '}
          bank statements.{' '}
          <Link
            href="https://support.joinheard.com/hc/en-us/articles/23569956149399-Bank-Statements-at-Heard"
            variant="secondary"
            newPage
          >
            Why?
          </Link>
        </Text>
        <Text>
          <b>
            {total} statement{total !== 1 ? 's' : ''}
          </b>{' '}
          {total !== 1 ? 'are' : 'is'} missing
        </Text>
        <ViewMissingStatementsAccordion
          title="View list of missing statements"
          neededStatements={needed}
        />
      </>
    )
  }, [fetching, isDone, total, needed])

  return (
    <ChecklistItemCard
      isDone={isDone}
      icon={uploadIcon(isPastCalc)}
      header="Upload your statements"
      onClick={() => {
        track('QTE Upload Bank Statements CTA Clicked')
        navigate('/practice/documents?tab=bank')
      }}
    >
      {content}
    </ChecklistItemCard>
  )
}

export const ReviewTxnCTA = ({
  isDone,
  count,
}: {
  isDone: boolean
  count: number
}) => {
  const navigate = useNavigate()
  const isPastCalc = useReselector(selectIsPastTaxEstimateCalculationDate)
  return (
    <ChecklistItemCard
      isDone={isDone}
      icon={bookIcon(isPastCalc)}
      header="Review your transactions"
      onClick={() => navigate('/transactions/review')}
    >
      <Text color={TextColors.MOSS}>
        {isDone ? (
          <>
            You&apos;re all caught up.{' '}
            <Link href="/transactions/all" variant="secondary">
              View transactions
            </Link>
          </>
        ) : (
          <>
            Get the most accurate estimate by making sure your transactions are
            up-to-date.
            <br />
            <b>
              {count} transaction{count !== 1 ? 's' : ''}
            </b>{' '}
            need{count === 1 ? 's' : ''} a note/categorization.
          </>
        )}
      </Text>
    </ChecklistItemCard>
  )
}

export const BooksStatus = ({
  booksReconciled,
}: {
  booksReconciled: boolean
}) => {
  return (
    <Card color={CardColors.LEVEL_1} className="px-8 py-2">
      <Text color={TextColors.MOSS} className="flex items-center">
        <Icon
          icon={booksReconciled ? faBookHeart : farBookOpenCover}
          className={`min-w-[16px] min-h-[16px] mr-2 ${booksReconciled ? TextColors.GREEN : TextColors.YELLOW}`}
        />
        <span>
          Your bookkeeper has indicated your books{' '}
          {booksReconciled ? (
            <>
              have been{' '}
              <span className="text-green-600 font-bold">reviewed.</span>
            </>
          ) : (
            <>
              are still{' '}
              <span className="text-yellow-600 font-bold">in review.</span>
            </>
          )}
        </span>
      </Text>
    </Card>
  )
}

export const BooksIncompleteInfo = () => (
  <Card color={CardColors.LEVEL_1} className="p-4">
    <div className="flex items-start gap-4">
      <Icon
        icon={faInfoCircle}
        className="min-w-[20px] min-h-[20px] mt-1 text-foreground"
      />
      <div>
        <Text variant={TypographyStyles.H3} className="mb-2">
          I&apos;ve clarified my transactions. Why are my books still in review?
        </Text>
        <List bulleted>
          <List.Item>
            <Text>
              If your bookkeeper needs more information, they&apos;ll reach out
              via Messages
            </Text>
          </List.Item>
          <List.Item>
            <Text>
              If you&apos;ve completed all of the steps, your bookkeeper may
              need more time to categorize your transactions
            </Text>
          </List.Item>
        </List>
      </div>
    </div>
  </Card>
)
